import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Tooltip,
    Grid,
    Card,
    CardContent
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    CreditCard as CreditCardIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getCreditCustomers, updateCreditLimit } from '../../services/api';
import './Credit.css';

const CreditCustomers = () => {
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [newCreditLimit, setNewCreditLimit] = useState('');
    const [updating, setUpdating] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchCreditCustomers();
    }, []);

    useEffect(() => {
        filterCustomers();
    }, [customers, searchTerm, filterStatus]);

    const fetchCreditCustomers = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await getCreditCustomers();
            // Ensure customers is always an array
            setCustomers(response?.data || []);
        } catch (err) {
            setError(err.message || 'Failed to load credit customers');
        } finally {
            setLoading(false);
        }
    };

    const filterCustomers = () => {
        let filtered = customers || [];
        
        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(customer => {
                if (!customer) return false;
                const name = customer.name || '';
                const code = customer.customer_code || '';
                const email = customer.email || '';
                const phone = customer.phone || '';
                
                return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       phone.toLowerCase().includes(searchTerm.toLowerCase());
            });
        }
        
        // Filter by status
        if (filterStatus !== 'all') {
            filtered = filtered.filter(customer => 
                customer?.credit_status === filterStatus
            );
        }
        
        setFilteredCustomers(filtered);
    };

    const handleOpenEditDialog = (customer) => {
        if (!customer) return;
        setSelectedCustomer(customer);
        setNewCreditLimit(customer.credit_limit || '');
        setEditDialogOpen(true);
    };

    const handleUpdateCreditLimit = async () => {
        if (!selectedCustomer || !newCreditLimit) return;
        
        setUpdating(true);
        try {
            await updateCreditLimit(selectedCustomer.id, {
                credit_limit: parseFloat(newCreditLimit)
            });
            
            // Update local state
            setCustomers(prev => prev.map(c => 
                c?.id === selectedCustomer.id 
                    ? { ...c, credit_limit: parseFloat(newCreditLimit) }
                    : c
            ));
            
            setEditDialogOpen(false);
        } catch (err) {
            setError(err.message || 'Failed to update credit limit');
        } finally {
            setUpdating(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'LKR 0.00';
        if (typeof amount === 'string' && amount.includes('LKR')) return amount;
        
        try {
            return new Intl.NumberFormat('en-LK', {
                style: 'currency',
                currency: 'LKR',
                minimumFractionDigits: 2
            }).format(parseFloat(amount) || 0);
        } catch {
            return 'LKR 0.00';
        }
    };

    const getStatusColor = (status) => {
        if (!status) return 'default';
        
        switch (status.toLowerCase()) {
            case 'active': return 'success';
            case 'blocked': return 'error';
            case 'suspended': return 'warning';
            case 'over_limit': return 'error';
            case 'closed': return 'default';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        if (!status) return null;
        
        switch (status.toLowerCase()) {
            case 'active': return <CheckCircleIcon fontSize="small" />;
            case 'blocked': return <BlockIcon fontSize="small" />;
            case 'suspended': return <WarningIcon fontSize="small" />;
            case 'over_limit': return <WarningIcon fontSize="small" />;
            default: return null;
        }
    };

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h4" component="h1">
                        <CreditCardIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                        Credit Customers
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/customers/new?credit=true')}
                    >
                        Add Credit Customer
                    </Button>
                </Box>
                <Typography variant="subtitle1" color="textSecondary">
                    Manage customer credit accounts, limits, and status
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" variant="body2" gutterBottom>
                                Total Credit Customers
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                                {Array.isArray(customers) ? customers.length : 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" variant="body2" gutterBottom>
                                Total Credit Limit
                            </Typography>
                            <Typography variant="h5" fontWeight="bold" color="primary">
                                {formatCurrency(
                                    Array.isArray(customers) 
                                        ? customers.reduce((sum, c) => sum + (c?.credit_limit || 0), 0)
                                        : 0
                                )}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" variant="body2" gutterBottom>
                                Total Outstanding
                            </Typography>
                            <Typography variant="h5" fontWeight="bold" color="error">
                                {formatCurrency(
                                    Array.isArray(customers)
                                        ? customers.reduce((sum, c) => sum + (c?.outstanding_balance || 0), 0)
                                        : 0
                                )}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" variant="body2" gutterBottom>
                                Active Accounts
                            </Typography>
                            <Typography variant="h5" fontWeight="bold" color="success.main">
                                {Array.isArray(customers)
                                    ? customers.filter(c => c?.credit_status === 'active').length
                                    : 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Search customers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            select
                            label="Status Filter"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FilterListIcon />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="blocked">Blocked</MenuItem>
                            <MenuItem value="suspended">Suspended</MenuItem>
                            <MenuItem value="over_limit">Over Limit</MenuItem>
                            <MenuItem value="closed">Closed</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ textAlign: 'right' }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={fetchCreditCustomers}
                            sx={{ mr: 1 }}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {/* Export functionality */}}
                        >
                            Export
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Customers Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Customer</TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell align="right">Credit Limit</TableCell>
                            <TableCell align="right">Available</TableCell>
                            <TableCell align="right">Outstanding</TableCell>
                            <TableCell align="center">Utilization</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(filteredCustomers) && filteredCustomers.map((customer, index) => (
                            customer && (
                                <TableRow key={customer.id || index} hover>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body1" fontWeight="medium">
                                                {customer?.name || 'N/A'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Code: {customer?.customer_code || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2">
                                                {customer?.phone || 'N/A'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {customer?.email || 'No email'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body1">
                                            {formatCurrency(customer?.credit_limit)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography 
                                            variant="body1"
                                            color={
                                                (customer?.available_credit || 0) < 
                                                ((customer?.credit_limit || 0) * 0.1) 
                                                    ? 'error' 
                                                    : 'success'
                                            }
                                        >
                                            {formatCurrency(customer?.available_credit)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body1" color="error">
                                            {formatCurrency(customer?.outstanding_balance)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ flexGrow: 1, bgcolor: '#ddd', height: 8, borderRadius: 4 }}>
                                                <Box 
                                                    sx={{ 
                                                        bgcolor: (customer?.credit_utilization || 0) > 80 ? '#f44336' : 
                                                                (customer?.credit_utilization || 0) > 60 ? '#ff9800' : '#4caf50',
                                                        width: `${Math.min(customer?.credit_utilization || 0, 100)}%`,
                                                        height: '100%',
                                                        borderRadius: 4
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="caption" fontWeight="bold">
                                                {customer?.credit_utilization || 0}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip 
                                            icon={getStatusIcon(customer?.credit_status)}
                                            label={customer?.credit_status?.toUpperCase() || 'UNKNOWN'}
                                            size="small"
                                            color={getStatusColor(customer?.credit_status)}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/credit/customers/${customer?.id}`)}
                                                disabled={!customer?.id}
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit Credit Limit">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenEditDialog(customer)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )
                        ))}
                        
                        {(!Array.isArray(filteredCustomers) || filteredCustomers.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                    <Typography color="textSecondary">
                                        {!Array.isArray(customers) || customers.length === 0 
                                            ? 'No credit customers found' 
                                            : 'No customers match your search'
                                        }
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Edit Credit Limit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Update Credit Limit</DialogTitle>
                <DialogContent>
                    {selectedCustomer && (
                        <Box sx={{ pt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Customer: <strong>{selectedCustomer?.name || 'N/A'}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Current Credit Limit: {formatCurrency(selectedCustomer?.credit_limit)}
                            </Typography>
                            
                            <TextField
                                fullWidth
                                label="New Credit Limit (LKR)"
                                type="number"
                                value={newCreditLimit}
                                onChange={(e) => setNewCreditLimit(e.target.value)}
                                margin="normal"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                                }}
                                helperText="Enter the new credit limit amount"
                            />
                            
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    Updating credit limit will automatically recalculate available credit.
                                </Typography>
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={updating}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpdateCreditLimit} 
                        variant="contained" 
                        disabled={updating || !newCreditLimit}
                        startIcon={updating ? <CircularProgress size={20} /> : null}
                    >
                        {updating ? 'Updating...' : 'Update Limit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default CreditCustomers;